// Globals
import { useTranslation } from 'react-i18next';

// Components
import {
  StyledBidCard,
  StyledBidInfo,
  StyledValueBlock,
  StyledBidInfoLabel,
  StyledBidInfoValue,
  StyledDivider,
  StyledBiddersSection,
  StyledBiddersTitle,
  StyledBidCardLabel,
  StyledBidCardValue,
  StyledFinancingSection,
  StyledFinancingTitle,
  StyledPrice,
  StyledRowWrapper,
  StyledColumnWrapper,
  StyledMobilePairWrapper,
  StyledStatusText,
} from './BidCardStyles';

// Models
import { IBidItem, BidStatusEnum } from '@/models/bidding.model';

// Helpers
import { renderCurrency, renderDate } from '@/utils';
import { getStatusLabel } from './helpers';
import { DateFormats } from '@/constants/dates';
import { useCountdown } from '../../hooks/useCountdown';

interface BidCardProps {
  bidContext: IBidItem;
  isModal?: boolean;
  ownerName?: string;
}

const BidCard = ({ bidContext, isModal = false, ownerName }: BidCardProps) => {
  const { t } = useTranslation('bidding');

  const countdown = useCountdown(bidContext?.bid_expires_at ?? '');
  const isExpiredByTime = countdown.isExpired && bidContext?.status === BidStatusEnum.CONFIRMED;
  const displayStatus = isExpiredByTime ? BidStatusEnum.EXPIRED : bidContext?.status;

  const statusLabel = getStatusLabel(t, displayStatus, ownerName);

  const showAlternativeFinancing =
    bidContext.is_alternative_financing === true || !!bidContext.alternative_financing_details?.trim();

  const alternativeFinancingDisplay = bidContext.alternative_financing_details?.trim()
    ? bidContext.alternative_financing_details
    : '—';

  return (
    <StyledBidCard $status={displayStatus} $isModal={isModal}>
      <StyledBidInfo $isModal={isModal}>
        <StyledColumnWrapper>
          <StyledPrice>{renderCurrency(bidContext?.bid_amount)}</StyledPrice>
          <StyledRowWrapper $isModal={isModal}>
            {bidContext?.bid_expires_at && (
              <StyledValueBlock>
                <StyledBidInfoLabel $status={displayStatus} $isModal={isModal}>
                  {t('bidding_bid_stands_to')}
                </StyledBidInfoLabel>
                <StyledBidInfoValue $status={displayStatus}>
                  {renderDate(bidContext?.bid_expires_at, DateFormats.DATE_WITH_TIME_WORDS)}
                </StyledBidInfoValue>
              </StyledValueBlock>
            )}
            <StyledValueBlock>
              <StyledBidInfoLabel $status={displayStatus} $isModal={isModal}>
                {t('bidding_preferred_takeover_date')}
              </StyledBidInfoLabel>
              <StyledBidInfoValue $status={displayStatus}>{bidContext?.preferred_takeover_date}</StyledBidInfoValue>
            </StyledValueBlock>
          </StyledRowWrapper>
          {bidContext?.reservation && (
            <StyledValueBlock>
              <StyledBidInfoLabel $status={displayStatus} $isModal={isModal}>
                {t('bidding_reservation')}
              </StyledBidInfoLabel>
              <StyledBidInfoValue $status={displayStatus}>{bidContext?.reservation}</StyledBidInfoValue>
            </StyledValueBlock>
          )}
        </StyledColumnWrapper>
        {statusLabel && <StyledStatusText $status={displayStatus}>{statusLabel}</StyledStatusText>}
      </StyledBidInfo>

      <StyledDivider />

      <StyledBiddersSection>
        <StyledBiddersTitle $status={displayStatus}>{t('bidding_bidders')}</StyledBiddersTitle>
        <div>
          <StyledValueBlock>
            <StyledBidCardLabel $status={displayStatus}>{t('bidding_bidder')}</StyledBidCardLabel>
            <StyledBidCardValue $status={displayStatus}>{bidContext?.bidder_full_name}</StyledBidCardValue>
          </StyledValueBlock>
          <StyledValueBlock>
            <StyledBidCardLabel $status={displayStatus}>{t('bidding_address')}</StyledBidCardLabel>
            <StyledBidCardValue $status={displayStatus}>
              {bidContext?.bidder_address?.full_address || '-'}
            </StyledBidCardValue>
          </StyledValueBlock>
          <StyledMobilePairWrapper $isModal={isModal}>
            <StyledValueBlock>
              <StyledBidCardLabel $status={displayStatus}>{t('bidding_phone_number')}</StyledBidCardLabel>
              <StyledBidCardValue $status={displayStatus}>{bidContext?.bidder_phone_number}</StyledBidCardValue>
            </StyledValueBlock>
            <StyledValueBlock>
              <StyledBidCardLabel $status={displayStatus}>{t('bidding_email')}</StyledBidCardLabel>
              <StyledBidCardValue $status={displayStatus}>{bidContext?.bidder_email}</StyledBidCardValue>
            </StyledValueBlock>
          </StyledMobilePairWrapper>
        </div>
        {bidContext?.co_bidders?.map((coBidder, index) => (
          <div key={coBidder.id || index}>
            <StyledValueBlock>
              <StyledBidCardLabel $status={displayStatus}>
                {t('bidding_co_bidder', { number: index + 1 })}
              </StyledBidCardLabel>
              <StyledBidCardValue $status={displayStatus}>{coBidder.full_name || '-'}</StyledBidCardValue>
            </StyledValueBlock>
            <StyledValueBlock>
              <StyledBidCardLabel $status={displayStatus}>{t('bidding_address')}</StyledBidCardLabel>
              <StyledBidCardValue $status={displayStatus}>{coBidder.address?.full_address || '-'}</StyledBidCardValue>
            </StyledValueBlock>
            <StyledMobilePairWrapper $isModal={isModal}>
              <StyledValueBlock>
                <StyledBidCardLabel $status={displayStatus}>{t('bidding_phone_number')}</StyledBidCardLabel>
                <StyledBidCardValue $status={displayStatus}>{coBidder.phone_number || '-'}</StyledBidCardValue>
              </StyledValueBlock>
              <StyledValueBlock>
                <StyledBidCardLabel $status={displayStatus}>{t('bidding_email')}</StyledBidCardLabel>
                <StyledBidCardValue $status={displayStatus}>{coBidder.email || '-'}</StyledBidCardValue>
              </StyledValueBlock>
            </StyledMobilePairWrapper>
          </div>
        ))}
      </StyledBiddersSection>

      <StyledDivider />

      <StyledFinancingSection>
        <StyledFinancingTitle $status={displayStatus}>{t('bidding_financing')}</StyledFinancingTitle>
        {showAlternativeFinancing ? (
          <StyledValueBlock>
            <StyledBidCardLabel $status={displayStatus}>
              {t('bidding_alternative_financing_details')}
            </StyledBidCardLabel>
            <StyledBidCardValue $status={displayStatus}>{alternativeFinancingDisplay}</StyledBidCardValue>
          </StyledValueBlock>
        ) : (
          <StyledRowWrapper $isModal={isModal}>
            <StyledMobilePairWrapper $isModal={isModal}>
              <StyledValueBlock>
                <StyledBidCardLabel $status={displayStatus}>{t('bidding_name_of_bank')}</StyledBidCardLabel>
                <StyledBidCardValue $status={displayStatus}>{bidContext?.bank_name}</StyledBidCardValue>
              </StyledValueBlock>
              <StyledValueBlock>
                <StyledBidCardLabel $status={displayStatus}>{t('bidding_contact_person')}</StyledBidCardLabel>
                <StyledBidCardValue $status={displayStatus}>{bidContext?.bank_contact_person}</StyledBidCardValue>
              </StyledValueBlock>
            </StyledMobilePairWrapper>
            <StyledMobilePairWrapper $isModal={isModal}>
              <StyledValueBlock>
                <StyledBidCardLabel $status={displayStatus}>{t('bidding_phone_number')}</StyledBidCardLabel>
                <StyledBidCardValue $status={displayStatus}>
                  {bidContext?.bank_contact_person_phone_number}
                </StyledBidCardValue>
              </StyledValueBlock>
              <StyledValueBlock>
                <StyledBidCardLabel $status={displayStatus}>{t('bidding_email')}</StyledBidCardLabel>
                <StyledBidCardValue $status={displayStatus}>{bidContext?.bank_contact_person_email}</StyledBidCardValue>
              </StyledValueBlock>
            </StyledMobilePairWrapper>
          </StyledRowWrapper>
        )}
      </StyledFinancingSection>
    </StyledBidCard>
  );
};

export default BidCard;
